/**
 * Route API errors to the Snackbar. Wired to the v2 adapter (webApiAdmin,
 * `admin/*` prefix) — the legacy `/api2 /api3` adapters were removed in the
 * v2-only migration.
 *
 * 401/session-expiry is handled by the environment fetch (getEnvironment) /
 * AuthContext, NOT at the adapter level: a spurious v2 401 (e.g. the AuthContext
 * keepalive) must not force a logout, so no unauthorized handler is wired here.
 */
import type { Store } from '@reduxjs/toolkit';
import webApiAdmin from './webApiAdmin';
import { showSnackbar, recordSnackbarSilently } from '@/store/slices/snackbarSlice';

interface ApiErrorToastInput {
  code?: number;
  message?: string;
  apiName?: string;
  apiPath?: string;
  method?: string;
  skipErrorToast?: boolean;
  errors?: unknown;
}

function serializeErrors(errors: unknown): string | undefined {
  if (errors == null) return undefined;
  if (typeof errors === 'string') return errors;
  try { return JSON.stringify(errors, null, 2); } catch { return String(errors); }
}

function buildApiErrorToastPayload(error: ApiErrorToastInput) {
  return {
    text: error.message ?? 'An error occurred',
    kind: 'api-error' as const,
    code: error.code,
    method: error.method,
    apiLabel: error.apiName ?? error.apiPath ?? '',
    timestamp: Date.now(),
    detail: serializeErrors(error.errors),
  };
}

export function setupApiErrorHandler(store: Store): void {
  const dispatchApiError = (error: ApiErrorToastInput) => {
    // 401은 별도 처리(아래 unauthorized handler)하므로 Snackbar 중복 표시 안 함
    if (error.code === 401) return;
    const payload = buildApiErrorToastPayload(error);
    if (error.skipErrorToast) {
      // 호출자가 자체 에러 UI를 띄우는 경우 — 토스트는 생략하고 ErrorBadge용 히스토리에만 기록
      store.dispatch(recordSnackbarSilently(payload));
    } else {
      store.dispatch(showSnackbar(payload));
    }
  };

  // Route the v2 adapter's errors through the Snackbar pipeline (replaces the
  // adapter's default alert()). No unauthorized handler is wired (see file
  // header): the environment fetch / AuthContext owns the 401/session lifecycle.
  webApiAdmin.setErrorDispatcher(dispatchApiError);
}
